/**
 * Candidate V2 variance components (combined gate R-6, Decision
 * Log Addendum 89) — the pooled one-way ANOVA decomposition behind
 * the two-stage psi, computed from a cell's respondent microdata.
 *
 * Construction (frozen at Addendum 89). Respondents of the cell
 * (outcome present for the indicator; subgroup cells restricted by
 * the direct stage's own subgroup rule; the outcome coded 1 when
 * it is 1, else 0) are grouped by area and, within area, by PSU
 * under the direct stage's composite key (stratum key + PSU id, so
 * PSUs are nested in strata by construction). An area contributes
 * only when it has at least 2 sampled PSUs in the cell. Pooled over
 * contributing areas, with SS_w the weighted within-PSU sum of
 * squares of the binary outcome, SS_b the weighted between-PSU sum
 * of squares about the area means, df_w = sum (n_d - G_d),
 * df_b = sum (G_d - 1), and n0 the pooled respondents per PSU:
 *   sigma2_w = SS_w / df_w        (0 when df_w = 0: single-stage)
 *   sigma2_b = max(0, (SS_b / df_b - sigma2_w) / n0)   [truncated]
 * Component design effects: deff_b is the pooled within-area
 * design effect of the cell computed from the domains'
 * WITH-REPLACEMENT variances exactly as deff.ts's 'within-area'
 * branch computes it without frame counts (ratio of sums, the
 * 0.05 stability floor), and
 *   deff_w2 = deff_b * sigma2_w / (n0 * sigma2_b + sigma2_w).
 * The area first-stage fraction f1_d comes from frame counts
 * (sampled PSUs over frame PSUs, summed over the area's strata;
 * strata with no frame entry are excluded from both sums; no
 * counts at all gives f1_d = 0). f1_d is clamped to [0, 1]: a
 * realized fraction above 1 is the direct stage's own certainty
 * case (f >= 1 there), read the same way here. The per-area
 * design effect is deff_d = (1 - f1_d) * deff_b + f1_d * deff_w2.
 *
 * Undefined (the function returns undefined, and candidate V2
 * does not run for the cell) when: no area contributes, df_b <= 0,
 * n0 * sigma2_b + sigma2_w = 0, or the with-replacement pooled
 * design effect has no usable pool.
 */
import { WITHIN_AREA_DEFF_FLOOR } from './deff.js';
import { STATEWIDE_AREA_ID } from './direct.js';
/** Mirror of direct.ts's stratum key (kept in step with it). */
const SINGLE_STRATUM = '__single_stratum__';
function stratumKeyOf(record) {
    return record.stratum === undefined || record.stratum === null
        ? SINGLE_STRATUM
        : String(record.stratum);
}
function psuKeyOf(record, rowIndex) {
    const psu = record.psu === undefined || record.psu === null
        ? `__row_${rowIndex}`
        : String(record.psu);
    return `${stratumKeyOf(record)}|${psu}`;
}
export function computeV2Components(opts) {
    const { respondents, indicatorId } = opts;
    /* ---- First-stage fractions (design-level: the full respondent set). */
    const sampledPsusByAreaStratum = new Map();
    respondents.forEach((record, rowIndex) => {
        let byStratum = sampledPsusByAreaStratum.get(record.areaId);
        if (byStratum === undefined) {
            byStratum = new Map();
            sampledPsusByAreaStratum.set(record.areaId, byStratum);
        }
        const sKey = stratumKeyOf(record);
        let set = byStratum.get(sKey);
        if (set === undefined) {
            set = new Set();
            byStratum.set(sKey, set);
        }
        set.add(psuKeyOf(record, rowIndex));
    });
    const f1ByArea = new Map();
    for (const [areaId, byStratum] of sampledPsusByAreaStratum) {
        let f1 = 0;
        if (opts.psuPopulationCounts !== undefined) {
            let num = 0;
            let den = 0;
            for (const [sKey, set] of byStratum) {
                const population = opts.psuPopulationCounts.get(sKey);
                if (population === undefined || !(population > 0))
                    continue;
                num += set.size;
                den += population;
            }
            if (den > 0)
                f1 = Math.min(1, Math.max(0, num / den));
        }
        f1ByArea.set(areaId, f1);
    }
    const areas = new Map();
    respondents.forEach((record, rowIndex) => {
        const outcome = record.outcomes[indicatorId];
        if (outcome === null || outcome === undefined)
            return;
        if (opts.subgroupVariable !== undefined) {
            const value = record.subgroups[opts.subgroupVariable];
            if (value === null || value === undefined)
                return;
            if (value !== opts.subgroupLevel &&
                String(value) !== String(opts.subgroupLevel)) {
                return;
            }
        }
        const y = outcome === 1 ? 1 : 0;
        const w = record.weight;
        let byPsu = areas.get(record.areaId);
        if (byPsu === undefined) {
            byPsu = new Map();
            areas.set(record.areaId, byPsu);
        }
        const pKey = psuKeyOf(record, rowIndex);
        let acc = byPsu.get(pKey);
        if (acc === undefined) {
            acc = { n: 0, w: 0, wy: 0 };
            byPsu.set(pKey, acc);
        }
        acc.n += 1;
        acc.w += w;
        acc.wy += w * y;
    });
    let ssW = 0;
    let ssB = 0;
    let dfW = 0;
    let dfB = 0;
    let totalN = 0;
    let totalG = 0;
    let contributingAreas = 0;
    for (const byPsu of areas.values()) {
        const g = byPsu.size;
        if (g < 2)
            continue;
        let nD = 0;
        let wD = 0;
        let wyD = 0;
        let sumY2OverW = 0; // sum_g (wy_g^2 / w_g), for both sums of squares
        let ssWArea = 0;
        for (const acc of byPsu.values()) {
            nD += acc.n;
            wD += acc.w;
            wyD += acc.wy;
            if (acc.w > 0) {
                sumY2OverW += (acc.wy * acc.wy) / acc.w;
                // Within-PSU sum of squares: sum_k w (y - p_g)^2 = wy_g - wy_g^2 / w_g.
                ssWArea += Math.max(0, acc.wy - (acc.wy * acc.wy) / acc.w);
            }
        }
        if (!(wD > 0))
            continue;
        contributingAreas += 1;
        ssW += ssWArea;
        // Between-PSU sum of squares about the area mean:
        // sum_g w_g (p_g - p_d)^2 = sum_g wy_g^2 / w_g - wy_d^2 / w_d.
        ssB += Math.max(0, sumY2OverW - (wyD * wyD) / wD);
        dfW += nD - g;
        dfB += g - 1;
        totalN += nD;
        totalG += g;
    }
    if (contributingAreas === 0 || dfB <= 0 || totalG === 0)
        return undefined;
    const n0 = totalN / totalG;
    const msB = ssB / dfB;
    const sigma2W = dfW > 0 ? ssW / dfW : 0;
    const sigma2B = Math.max(0, (msB - sigma2W) / n0);
    const componentDenom = n0 * sigma2B + sigma2W;
    if (!(componentDenom > 0))
        return undefined;
    /* ---- deff_b: deff.ts's 'within-area' pooling on with-replacement variances. */
    let num = 0;
    let den = 0;
    for (const est of opts.cellDirectEstimates) {
        if (est.domain.areaId === STATEWIDE_AREA_ID)
            continue;
        if (!(est.n > 0) || !(est.estimate > 0 && est.estimate < 1))
            continue;
        const v = est.variance;
        if (v === undefined || !Number.isFinite(v) || v < 0)
            continue;
        num += v;
        den += (est.estimate * (1 - est.estimate)) / est.n;
    }
    const pooled = den > 0 ? num / den : Number.NaN;
    if (!Number.isFinite(pooled) || !(pooled > 0))
        return undefined;
    const deffB = Math.max(pooled, WITHIN_AREA_DEFF_FLOOR);
    const deffW2 = (deffB * sigma2W) / componentDenom;
    const deffByArea = new Map();
    for (const [areaId, f1] of f1ByArea) {
        deffByArea.set(areaId, (1 - f1) * deffB + f1 * deffW2);
    }
    return {
        deffB,
        deffW2,
        sigma2B,
        sigma2W,
        n0,
        ssB,
        ssW,
        dfB,
        dfW,
        contributingAreas,
        f1ByArea,
        deffByArea,
    };
}
//# sourceMappingURL=v2components.js.map